import React, { useState, useEffect } from 'react';
import pako from 'pako';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Grid,
  Card,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  CircularProgress,
  Menu,
  Select
} from '@material-ui/core';
import {
  Refresh as RefreshIcon,
  Visibility,
  ExpandMore,
  ExpandLess,
  FilterList,
  Business,
  Security,
  Assessment,
  CloudDone,
  Info as InfoIcon,
  Apps as AppsIcon,
  MoreVert,
  FileCopy as CopyIcon,
  VisibilityOff,
  Storage as StorageIcon
} from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { HoverTableRow } from '../ssh/SshStyles';
import base from '../../config';
import { headers } from '../../helper/api';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  sectionTitle: {
    color: '#6a1b9a',
    fontWeight: 'bold',
  },
  statusChip: {
    fontWeight: 'normal',
  },
  inUseStatus: { backgroundColor: '#4caf50', color: 'white' },
  availableStatus: { backgroundColor: '#2196f3', color: 'white' },
  inProgressStatus: { backgroundColor: '#ff9800', color: 'white' },
  terminateInProgressStatus: { backgroundColor: '#ff5722', color: 'white' },
  processingStatus: { backgroundColor: '#9c27b0', color: 'white' },
  terminatedStatus: { backgroundColor: '#9e9e9e', color: 'white' },
  prodEnvironment: { backgroundColor: '#f44336', color: 'white' },
  uatEnvironment: { backgroundColor: '#ff9800', color: 'white' },
  devEnvironment: { backgroundColor: '#4caf50', color: 'white' },
  tableContainer: {
    marginTop: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 340px)',
    minHeight: 200,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  statsContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: theme.spacing(1),
    border: '1px solid #e9ecef',
    width: '100%',
    boxSizing: 'border-box',
  },
  statCard: {
    textAlign: 'center',
    padding: theme.spacing(1.5),
    backgroundColor: 'white',
    borderRadius: theme.spacing(0.5),
    border: '1px solid #dee2e6',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    flex: '1 1 150px',
    minWidth: 130,
  },
  detailsCard: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: 'white',
    border: '1px solid #e3f2fd',
    borderRadius: theme.spacing(1),
  },
  secretRow: {
    marginBottom: theme.spacing(1.5),
    width: '100%',
  },
  secretLabel: {
    marginBottom: 4,
  },
  secretField: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    backgroundColor: '#f5f5f5',
    padding: '6px 8px',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    wordBreak: 'break-all',
    overflowWrap: 'anywhere',
  },
});

// ---- Helpers ----

// Returns display value — treats "-" as N/A
const val = function (v) {
  if (!v || v === '-') return 'N/A';
  return v;
};

// Backend gzip-compresses + base64-encodes large payloads (e.g. 6.72MB -> much
// smaller) and returns { compressed: true, data: "<base64 gzip>", compression_info }.
// This decodes base64 -> bytes -> gunzip (pako) -> UTF-8 string -> JSON.
const decompressData = function (compressedBase64Data) {
  try {
    var compressedBytes = atob(compressedBase64Data);
    var compressedArray = new Uint8Array(compressedBytes.length);
    for (var i = 0; i < compressedBytes.length; i++) {
      compressedArray[i] = compressedBytes.charCodeAt(i);
    }
    var decompressedArray = pako.inflate(compressedArray);
    var decompressedString = new TextDecoder().decode(decompressedArray);
    return JSON.parse(decompressedString);
  } catch (error) {
    console.error('Decompression error:', error);
    throw new Error('Failed to decompress data: ' + error.message);
  }
};

const maskSecret = function (value) {
  if (!value || value === '-') return 'N/A';
  if (value.indexOf('•') !== -1) return value;
  var visible = value.slice(-4);
  // Fixed-length mask (not one dot per character) so long keys don't wrap
  // across several lines — the copy button still copies the real full value.
  return '•'.repeat(16) + visible;
};

const OpenAipage = function (props) {
  var classes = props.classes;

  var servicesState = useState([]);
  var services = servicesState[0];
  var setServices = servicesState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var decompressionStatusState = useState('');
  var decompressionStatus = decompressionStatusState[0];
  var setDecompressionStatus = decompressionStatusState[1];

  var filtersState = useState({ environment: '', status: '', clientName: '' });
  var filters = filtersState[0];
  var setFilters = filtersState[1];

  var selectedServiceState = useState(null);
  var selectedService = selectedServiceState[0];
  var setSelectedService = selectedServiceState[1];

  var expandedRowsState = useState(new Set());
  var expandedRows = expandedRowsState[0];
  var setExpandedRows = expandedRowsState[1];

  var dialogOpenState = useState(false);
  var dialogOpen = dialogOpenState[0];
  var setDialogOpen = dialogOpenState[1];

  var showRawDataState = useState(false);
  var showRawData = showRawDataState[0];
  var setShowRawData = showRawDataState[1];

  var showSecretsState = useState(false);
  var showSecrets = showSecretsState[0];
  var setShowSecrets = showSecretsState[1];

  var showFiltersState = useState(false);
  var showFilters = showFiltersState[0];
  var setShowFilters = showFiltersState[1];

  var pageState = useState(0);
  var page = pageState[0];
  var setPage = pageState[1];

  var rowsPerPageState = useState(10);
  var rowsPerPage = rowsPerPageState[0];
  var setRowsPerPage = rowsPerPageState[1];

  var actionMenuAnchorState = useState(null);
  var actionMenuAnchor = actionMenuAnchorState[0];
  var setActionMenuAnchor = actionMenuAnchorState[1];

  var selectedServiceForActionState = useState(null);
  var selectedServiceForAction = selectedServiceForActionState[0];
  var setSelectedServiceForAction = selectedServiceForActionState[1];

  var statsState = useState({ total: 0, inUse: 0, available: 0, inProgress: 0, terminated: 0 });
  var stats = statsState[0];
  var setStats = statsState[1];

  useEffect(function () {
    fetchServices();
  }, []);

  var fetchServices = async function () {
    setLoading(true);
    try {
      var idToken = localStorage.getItem('token');
      var response = await fetch(`${base.app.apiSite}openAI_data`, {
        method: 'GET',
        headers: Object.assign({}, headers, {
          Authorization: idToken
        })
      });

      if (response.ok) {
        var result = await response.json();
        var servicesList;

        if (result.compressed) {
          // Large payloads (e.g. 1000+ records) come back gzip+base64 encoded.
          setDecompressionStatus('Decompressing response data...');
          try {
            var actualData = decompressData(result.data);
            servicesList = actualData.items || actualData.data || [];
            if (result.compression_info) {
              console.log(
                'Data decompressed: ' +
                result.compression_info.original_size_mb + 'MB -> ' +
                result.compression_info.compressed_size_mb + 'MB'
              );
            }
          } catch (decompError) {
            console.error('Decompression failed:', decompError);
            throw new Error('Failed to decompress response data');
          } finally {
            setDecompressionStatus('');
          }
        } else {
          // API returns { count: N, items: [...] } when not compressed
          servicesList = result.items || result.data || [];
        }

        if (!Array.isArray(servicesList)) {
          servicesList = [servicesList];
        }
        setServices(servicesList);
        computeStats(servicesList);
      } else {
        throw new Error('HTTP ' + response.status + ': ' + response.statusText);
      }
    } catch (error) {
      console.error('Error fetching OpenAI services:', error);
    } finally {
      setLoading(false);
    }
  };

  var computeStats = function (list) {
    setStats({
      total: list.length,
      inUse: list.filter(function (s) { return s.Status === 'In-use'; }).length,
      available: list.filter(function (s) { return s.Status === 'Available'; }).length,
      inProgress: list.filter(function (s) { return s.Status === 'In-progress' || s.Status === 'Processing'; }).length,
      terminated: list.filter(function (s) { return s.Status === 'Terminated' || s.Status === 'Terminate-In-Progress'; }).length,
    });
  };

  var handleViewDetails = function (service) {
    setSelectedService(service);
    setShowSecrets(false);
    setShowRawData(false);
    setDialogOpen(true);
  };

  var getStatusStyle = function (status) {
    switch (status) {
      case 'In-use': return { backgroundColor: '#4caf50', color: 'white' };
      case 'Available': return { backgroundColor: '#2196f3', color: 'white' };
      case 'In-progress': return { backgroundColor: '#ff9800', color: 'white' };
      case 'Terminate-In-Progress': return { backgroundColor: '#ff5722', color: 'white' };
      case 'Processing': return { backgroundColor: '#9c27b0', color: 'white' };
      case 'Terminated': return { backgroundColor: '#9e9e9e', color: 'white' };
      default: return { backgroundColor: '#9e9e9e', color: 'white' };
    }
  };

  var getEnvironmentStyle = function (environment) {
    switch (environment) {
      case 'Production': return { backgroundColor: '#f44336', color: 'white' };
      case 'UAT': return { backgroundColor: '#ff9800', color: 'white' };
      case 'Dev': return { backgroundColor: '#4caf50', color: 'white' };
      default: return { backgroundColor: '#9e9e9e', color: 'white' };
    }
  };

  // FIX (issue 3): @material-ui/core v4's TablePagination uses onChangePage /
  // onChangeRowsPerPage (NOT onPageChange / onRowsPerPageChange, which are the
  // v5/MUI-X prop names). Because the prop names didn't match the installed
  // v4 API, clicking the next-page arrow never fired this handler, so page
  // state never advanced past 0 and only the first 10 rows ever showed.
  var hasActiveFilters = filters.environment !== '' || filters.status !== '' || filters.clientName !== '';

  var handleClearFilters = function () {
    setFilters({ environment: '', status: '', clientName: '' });
    setPage(0);
  };

  var handleChangePage = function (event, newPage) {
    setPage(newPage);
  };

  var handleChangeRowsPerPage = function (event) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  var handleRowExpand = function (serviceName) {
    var newExpanded = new Set(expandedRows);
    if (newExpanded.has(serviceName)) {
      newExpanded.delete(serviceName);
    } else {
      newExpanded.add(serviceName);
    }
    setExpandedRows(newExpanded);
  };

  var handleActionMenuOpen = function (event, service) {
    setActionMenuAnchor(event.currentTarget);
    setSelectedServiceForAction(service);
  };

  var handleActionMenuClose = function () {
    setActionMenuAnchor(null);
    setSelectedServiceForAction(null);
  };

  var handleMenuAction = function (action) {
    if (!selectedServiceForAction) return;
    switch (action) {
      case 'view-details':
        handleViewDetails(selectedServiceForAction);
        break;
      case 'expand':
        handleRowExpand(selectedServiceForAction.OpenAIServiceName);
        break;
      default:
        break;
    }
    handleActionMenuClose();
  };

  var handleCopyToClipboard = function (value) {
    if (navigator.clipboard && value) {
      navigator.clipboard.writeText(value);
    }
  };

  var formatDate = function (dateString) {
    if (!dateString || dateString === '-') return 'N/A';
    var d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleString();
  };

  // DeploymentName is an array of objects: { Deployment_Name, Model, Version, "Capacity(K TPM)" }
  var getDeployments = function (service) {
    var dn = service.DeploymentName;
    if (!dn) return [];
    if (Array.isArray(dn)) return dn;
    return [];
  };

  var filteredServices = services.filter(function (service) {
    var envMatch = filters.environment === '' || service.Environment === filters.environment;
    var statusMatch = filters.status === '' || service.Status === filters.status;
    var clientMatch = filters.clientName === '' || (
      service.ClientName &&
      service.ClientName !== '-' &&
      service.ClientName.toLowerCase().includes(filters.clientName.toLowerCase())
    );
    return envMatch && statusMatch && clientMatch;
  });

  var displayedServices = filteredServices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Small reusable block for a secret row: label stacked above the value so
  // long labels ("SP Secret Key:") never collide with the dotted mask field
  // (FIX for issue 2 — the "crossing" text seen in the screenshot).
  var renderSecretRow = function (label, value) {
    return (
      <Box className={classes.secretRow}>
        <Typography variant="body2" className={classes.secretLabel}>
          <strong>{label}:</strong>
        </Typography>
        <span className={classes.secretField}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {showSecrets ? val(value) : maskSecret(value)}
          </span>
          <IconButton size="small" onClick={function () { handleCopyToClipboard(value); }} style={{ flexShrink: 0 }}>
            <CopyIcon style={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Box>
    );
  };

  return (
    <Box className={classes.root}>

      {/* Header */}
      <Box style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, gap: 12 }}>
        <Typography variant="h4" className={classes.sectionTitle}>
          OpenAI Service Provisioning Management
        </Typography>
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
          {hasActiveFilters && (
            <Button
              size="small"
              onClick={handleClearFilters}
              variant="outlined"
              style={{ borderColor: '#6a1b9a', color: '#6a1b9a' }}
            >
              Clear Filters
            </Button>
          )}
          <Button
            startIcon={<FilterList />}
            onClick={function () { setShowFilters(!showFilters); }}
            variant="outlined"
            style={{ borderColor: '#6a1b9a', color: '#6a1b9a' }}
          >
            Filters
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchServices}
            variant="contained"
            disabled={loading}
            style={{ backgroundColor: '#6a1b9a', color: 'white' }}
          >
            Refresh Data
          </Button>
        </Box>
      </Box>

      {/* Statistics Overview */}
      <Box className={classes.statsContainer}>
        <Typography variant="h6" className={classes.sectionTitle} gutterBottom>
          Service Status Overview
        </Typography>
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%' }}>
          <Box className={classes.statCard}>
            <Typography variant="h4" style={{ color: '#6a1b9a', fontWeight: 'bold' }}>{stats.total}</Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>Total Services</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography variant="h4" style={{ color: '#4caf50', fontWeight: 'bold' }}>{stats.inUse}</Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>In-use</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography variant="h4" style={{ color: '#2196f3', fontWeight: 'bold' }}>{stats.available}</Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>Available</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography variant="h4" style={{ color: '#ff9800', fontWeight: 'bold' }}>{stats.inProgress}</Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>In Progress</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography variant="h4" style={{ color: '#9e9e9e', fontWeight: 'bold' }}>{stats.terminated}</Typography>
            <Typography variant="caption" style={{ color: '#666', textTransform: 'uppercase' }}>Terminated</Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Box style={{ marginBottom: 24, padding: 16, backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
          <Box style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" className={classes.sectionTitle} gutterBottom>
              Filter Options
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search Client Name"
                value={filters.clientName}
                onChange={function (e) { setFilters(Object.assign({}, filters, { clientName: e.target.value })); }}
                variant="outlined"
                size="small"
                placeholder="Enter client name to search..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Environment</InputLabel>
                <Select
                  value={filters.environment}
                  onChange={function (e) { setFilters(Object.assign({}, filters, { environment: e.target.value })); }}
                  label="Environment"
                >
                  <MenuItem value="">All Environments</MenuItem>
                  <MenuItem value="Dev">Development</MenuItem>
                  <MenuItem value="UAT">UAT</MenuItem>
                  <MenuItem value="Production">Production</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={function (e) { setFilters(Object.assign({}, filters, { status: e.target.value })); }}
                  label="Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="In-use">In-use</MenuItem>
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="In-progress">In-progress</MenuItem>
                  <MenuItem value="Terminate-In-Progress">Terminate-In-Progress</MenuItem>
                  <MenuItem value="Processing">Processing</MenuItem>
                  <MenuItem value="Terminated">Terminated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Services Table */}
      <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', backgroundColor: '#fff', marginBottom: '16px', width: '100%', boxSizing: 'border-box' }}>
        <TableContainer className={classes.tableContainer}>
          {loading ? (
            <Box className={classes.loadingContainer} style={{ flexDirection: 'column', gap: 8 }}>
              <CircularProgress />
              {decompressionStatus && (
                <Typography variant="body2" color="textSecondary">
                  {decompressionStatus}
                </Typography>
              )}
            </Box>
          ) : filteredServices.length === 0 ? (
            <Box style={{ textAlign: 'center', padding: '48px 32px', color: '#666', backgroundColor: '#f8f9fa' }}>
              <InfoIcon style={{ fontSize: 48, marginBottom: 16, color: '#ccc' }} />
              <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                No OpenAI Services Found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {hasActiveFilters ?
                  'Try adjusting your filters to see more results.' :
                  'No services have been provisioned yet.'
                }
              </Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  style={{ color: '#6a1b9a', marginTop: 12 }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <Table stickyHeader size="small" style={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Service Name</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Client Name</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Application</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Az Region</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Environment</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Status</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Deployments</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>SP Expiry Date</TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: '#6a1b9a' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedServices.map(function (service) {
                  var deployments = getDeployments(service);
                  var isExpanded = expandedRows.has(service.OpenAIServiceName);
                  return (
                    <React.Fragment key={service.OpenAIServiceName}>
                      <HoverTableRow>
                        <TableCell style={{ maxWidth: 220, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                          {service.OpenAIServiceName}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {val(service.ClientName)}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ maxWidth: 160 }}>
                          <Typography variant="body2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {val(service.ApplicationName)}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ fontSize: '0.8rem' }}>
                          {val(service.AzRegion)}
                        </TableCell>
                        <TableCell>
                          {service.Environment && service.Environment !== '-' ? (
                            <Chip
                              label={service.Environment}
                              size="small"
                              className={classes.statusChip}
                              style={{ fontSize: '0.7rem', ...getEnvironmentStyle(service.Environment) }}
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {service.Status && service.Status !== '-' ? (
                            <Chip
                              label={service.Status}
                              size="small"
                              className={classes.statusChip}
                              style={{ ...getStatusStyle(service.Status) }}
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell style={{ fontSize: '0.8rem' }}>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {deployments.length} model{deployments.length === 1 ? '' : 's'}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ fontSize: '0.8rem' }}>
                          {formatDate(service.SPExpiryDate)}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={function () { handleRowExpand(service.OpenAIServiceName); }}
                            style={{ color: '#6a1b9a', marginRight: 4 }}
                            title="Expand / Collapse Deployments"
                          >
                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={function (e) { handleActionMenuOpen(e, service); }}
                            style={{ color: '#6a1b9a', backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}
                            title="More Actions"
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </HoverTableRow>

                      {/* Expanded deployments row */}
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }} colSpan={9}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box style={{
                              margin: '8px 0',
                              padding: '16px 20px',
                              backgroundColor: '#ffffff',
                              border: '1px solid #e3f2fd',
                              borderRadius: '8px',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                              <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 12, color: '#6a1b9a', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <StorageIcon fontSize="small" />
                                Model Deployments ({deployments.length})
                              </Typography>

                              {deployments.length > 0 ? (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Deployment Name</TableCell>
                                      <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Model</TableCell>
                                      <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Version</TableCell>
                                      <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Capacity (K TPM)</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {deployments.map(function (d, idx) {
                                      return (
                                        <TableRow key={idx}>
                                          <TableCell style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{d.Deployment_Name || 'N/A'}</TableCell>
                                          <TableCell style={{ fontSize: '0.78rem' }}>{d.Model || 'N/A'}</TableCell>
                                          <TableCell style={{ fontSize: '0.78rem' }}>{d.Version || 'N/A'}</TableCell>
                                          <TableCell style={{ fontSize: '0.78rem' }}>{d['Capacity(K TPM)'] || 'N/A'}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="textSecondary">No deployments configured.</Typography>
                              )}

                              {/* Quick info below deployments */}
                              <Grid container spacing={3} style={{ marginTop: 16 }}>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>RESOURCE GROUP</Typography>
                                  <Typography variant="body2">{val(service.ResourceGroupName)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>SUBSCRIPTION</Typography>
                                  <Typography variant="body2">{val(service.AzSubscriptionName)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="overline" style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>DEPLOYMENT ID</Typography>
                                  <Typography variant="body2">{val(service.DeploymentID)}</Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </div>

      {/* Pagination */}
      {!loading && filteredServices.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 25, 50]}
          component="div"
          count={filteredServices.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {/* Details Dialog */}
      <Dialog open={dialogOpen} onClose={function () { setDialogOpen(false); }} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">OpenAI Service Details</Typography>
            <Button variant="outlined" size="small" onClick={function () { setShowRawData(!showRawData); }}>
              {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {selectedService && (
            <Box>
              <Typography variant="h5" gutterBottom style={{ color: '#1976d2', fontWeight: 600, marginBottom: 24, wordBreak: 'break-word' }}>
                {val(selectedService.ClientName)} — {selectedService.OpenAIServiceName}
              </Typography>

              {/* Client & Application */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <Business style={{ marginRight: 8 }} />
                  Client & Application Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Client Name:</strong> {val(selectedService.ClientName)}</Typography>
                    <Typography variant="body2"><strong>Application Name:</strong> {val(selectedService.ApplicationName)}</Typography>
                    <Typography variant="body2"><strong>Air ID:</strong> {val(selectedService.AirID)}</Typography>
                    <Typography variant="body2"><strong>Contract ID:</strong> {val(selectedService.ContractID)}</Typography>
                    <Typography variant="body2"><strong>WBSE Code:</strong> {val(selectedService.WBSECode)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Order ID:</strong> {val(selectedService.OrderId)}</Typography>
                    <Typography variant="body2"><strong>Environment:</strong> {val(selectedService.Environment)}</Typography>
                    <Typography variant="body2"><strong>Status:</strong> {val(selectedService.Status)}</Typography>
                    <Typography variant="body2"><strong>Rotation Status:</strong> {val(selectedService.rotation_status)}</Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Infrastructure */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <CloudDone style={{ marginRight: 8 }} />
                  Infrastructure Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Azure Region:</strong> {val(selectedService.AzRegion)}</Typography>
                    <Typography variant="body2"><strong>Az Subscription Name:</strong> {val(selectedService.AzSubscriptionName)}</Typography>
                    <Typography variant="body2"><strong>Resource Group:</strong> {val(selectedService.ResourceGroupName)}</Typography>
                    <Typography variant="body2"><strong>Tenant ID:</strong> {val(selectedService.TenantID)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Deployment ID:</strong> {val(selectedService.DeploymentID)}</Typography>
                    <Typography variant="body2"><strong>Key Vault Name:</strong> {val(selectedService.KeyVaultName)}</Typography>
                    <Typography variant="body2"><strong>Key Vault Secret Name:</strong> {val(selectedService.KeyVaultOAISecretName)}</Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Endpoints & URLs */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <Assessment style={{ marginRight: 8 }} />
                  Endpoints & URLs
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 4, wordBreak: 'break-all' }}><strong>OpenAI Endpoint:</strong> {val(selectedService.OpenAIEndPoint)}</Typography>
                <Typography variant="body2" style={{ marginBottom: 4, wordBreak: 'break-all' }}><strong>Foundry URL:</strong> {val(selectedService.FoundryURL)}</Typography>
                <Typography variant="body2" style={{ marginBottom: 4, wordBreak: 'break-all' }}><strong>URL:</strong> {val(selectedService.URL)}</Typography>
                <Typography variant="body2" style={{ marginBottom: 4, wordBreak: 'break-all' }}><strong>APIM Base URL:</strong> {val(selectedService.apim_baseurl)}</Typography>
                <Typography variant="body2" style={{ wordBreak: 'break-all' }}><strong>APIM Auth URL:</strong> {val(selectedService.apim_auth_url)}</Typography>
              </Card>

              {/* Secrets & Credentials */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <Typography variant="h6" style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                    <Security style={{ marginRight: 8 }} />
                    Secrets & Credentials
                  </Typography>
                  <Button
                    size="small"
                    startIcon={showSecrets ? <VisibilityOff /> : <Visibility />}
                    onClick={function () { setShowSecrets(!showSecrets); }}
                  >
                    {showSecrets ? 'Mask' : 'Reveal'}
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" style={{ marginBottom: 10 }}>
                      <strong>SP ID:</strong> {val(selectedService.SPID)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" style={{ marginBottom: 10 }}>
                      <strong>SP Expiry Date:</strong> {formatDate(selectedService.SPExpiryDate)}
                    </Typography>
                  </Grid>
                </Grid>
                {/* One below another (full width) so keys never sit side by side */}
                <Box style={{ marginTop: 4 }}>
                  {renderSecretRow('OpenAI Key', selectedService.OpenAIKey)}
                  {renderSecretRow('SP Secret Key', selectedService.SPSecretKey)}
                </Box>
              </Card>

              {/* Model Deployments */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <AppsIcon style={{ marginRight: 8 }} />
                  Model Deployments ({getDeployments(selectedService).length})
                </Typography>
                {getDeployments(selectedService).length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Deployment Name</TableCell>
                        <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Model</TableCell>
                        <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Version</TableCell>
                        <TableCell style={{ fontWeight: 600, fontSize: '0.75rem', color: '#555' }}>Capacity (K TPM)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDeployments(selectedService).map(function (d, idx) {
                        return (
                          <TableRow key={idx}>
                            <TableCell style={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{d.Deployment_Name || 'N/A'}</TableCell>
                            <TableCell style={{ fontSize: '0.78rem' }}>{d.Model || 'N/A'}</TableCell>
                            <TableCell style={{ fontSize: '0.78rem' }}>{d.Version || 'N/A'}</TableCell>
                            <TableCell style={{ fontSize: '0.78rem' }}>{d['Capacity(K TPM)'] || 'N/A'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="textSecondary">No deployments configured.</Typography>
                )}
              </Card>

              {/* Instance Info */}
              <Card className={classes.detailsCard} style={{ marginBottom: 16 }}>
                <Typography variant="h6" gutterBottom style={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                  <InfoIcon style={{ marginRight: 8 }} />
                  Additional Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Instance IP:</strong> {val(selectedService.InstanceIP)}</Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Raw Data */}
              {showRawData && (
                <Card className={classes.detailsCard} style={{ marginTop: 16 }}>
                  <Typography variant="h6" gutterBottom style={{ color: '#1976d2' }}>Raw JSON Data</Typography>
                  <Box style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.8rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {JSON.stringify(
                        Object.assign({}, selectedService, {
                          OpenAIKey: showSecrets ? selectedService.OpenAIKey : maskSecret(selectedService.OpenAIKey),
                          SPSecretKey: showSecrets ? selectedService.SPSecretKey : maskSecret(selectedService.SPSecretKey),
                        }),
                        null,
                        2
                      )}
                    </pre>
                  </Box>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={function () { setDialogOpen(false); }} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu — View Details + View Deployments only */}
      <Menu
        anchorEl={actionMenuAnchor}
        keepMounted
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        PaperProps={{ style: { boxShadow: '0 4px 20px rgba(0,0,0,0.15)', borderRadius: 8, minWidth: 200 } }}
      >
        <MenuItem onClick={function () { handleMenuAction('view-details'); }} style={{ padding: '8px 16px' }}>
          <Visibility fontSize="small" style={{ color: '#1976d2', marginRight: 12 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={function () { handleMenuAction('expand'); }} style={{ padding: '8px 16px' }}>
          <AppsIcon fontSize="small" style={{ color: '#ff9800', marginRight: 12 }} />
          View Deployments
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default withStyles(styles)(OpenAipage);